import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import {FmlxTextBox, FmlxIcon, FmlxTooltip} from 'fmlx-common-ui';
import withIntl from '../../../../lib/withIntl';

const FloatingNumericBox = React.forwardRef(
  ({id, position, value, onBlur, label, disabled, hidden, maxValue, warningTooltip, intl}, ref) => {
    const [valueState, setValueState] = useState(value || 0);
    const [isExceedingInstrumentCapacity, setIsExceedingInstrumentCapacity] = useState(value > 9999);

    const handleChanges = (newVal) => {
      const newValNumber = Number(newVal);
      const newValPrecised =
        newVal.includes('.') && newVal[newVal.length - 1] !== '.' ? Number(newValNumber.toFixed(1)) : newVal;

      const isValid = !/[a-zA-Z]|[!@=#$%^&*+/,\\]/.test(newVal);
      const isNumber = !Number.isNaN(newValNumber);

      if ((isValid && isNumber) || newVal === '') {
        setValueState(newValPrecised);

        const isImpossible = newValPrecised > 9999;
        if (isExceedingInstrumentCapacity !== isImpossible) setIsExceedingInstrumentCapacity(isImpossible);
      }
    };

    const onBlurHandle = useCallback(
      (e) => {
        onBlur(e.value, id);
      },
      [id, onBlur],
    );

    const getTooltipLabel = () => {
      if (warningTooltip?.isShow) return warningTooltip?.label;

      if (isExceedingInstrumentCapacity) {
        return intl.formatMessage({
          id: 'protocolEditor.serialDilution.volumeTextBox.tooltip.exceedingInstrumentCapacity.label',
        });
      }

      if (valueState > maxValue && maxValue) {
        return intl.formatMessage({
          id: 'protocolEditor.serialDilution.volumeTextBox.tooltip.exceedingTotalVolume.label',
        });
      }

      return '';
    };

    return (
      <span
        className="floating-numeric-box"
        style={{top: position?.y, left: position?.x, display: 'flex', flexDirection: 'column'}}
        ref={ref}
      >
        {!hidden && (
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <span>{label}</span>
            <span
              className={`floating-numeric-box__input
              ${
                (valueState > maxValue && maxValue && !isExceedingInstrumentCapacity) ||
                (warningTooltip?.isShow && warningTooltip?.variant === 'warning')
                  ? 'warning'
                  : ''
              }
              ${isExceedingInstrumentCapacity || (warningTooltip?.isShow && warningTooltip?.variant !== 'warning') ? 'error' : ''}`}
            >
              <FmlxTooltip title={getTooltipLabel()}>
                <span>
                  <FmlxTextBox
                    value={valueState}
                    onChange={(e) => handleChanges(e.value)}
                    onBlur={onBlurHandle}
                    unit="μL"
                    decoration="unit"
                    size="sm"
                    disabled={disabled}
                    onEnterKeyPressed={(e) => onBlur(e.value, id)}
                  />
                </span>
              </FmlxTooltip>
            </span>
            {warningTooltip?.isShow && warningTooltip.variant !== 'warning' && (
              <span className="floating-numeric-box__info-tooltip">
                <FmlxTooltip title={warningTooltip?.label}>
                  <span>
                    <FmlxIcon name="Info" customColor="red" size="xs" />
                  </span>
                </FmlxTooltip>
              </span>
            )}
          </div>
        )}
      </span>
    );
  },
);

FloatingNumericBox.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.any,
  position: PropTypes.object,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  hidden: PropTypes.bool,
  maxValue: PropTypes.number,
  warningTooltip: PropTypes.object,
  intl: PropTypes.any.isRequired,
};

FloatingNumericBox.defaultProps = {
  id: '',
  label: '',
  position: {x: 0, y: 0},
  warningTooltip: {isShow: false, text: ''},
  value: null,
  onBlur: () => {},
  disabled: false,
  hidden: false,
  maxValue: null,
};

export default withIntl(FloatingNumericBox);
